/** 充值相关 */
export enum DepositReason {
  /** USDT充值 */
  DEPOSIT = 10,
  /** 站内转账(出) */
  TRANSFER_OUT = 210,
  /** 站内转账(进) */
  TRANSFER_IN = 211,
}

/** 提现相关 */
export enum WithdrawReason {
  /** 提现冻结 */
  FREEZE = 101,
  /** 提现解冻 */
  RELEASE = 102,
  /** 提现核销 */
  WRITE_OFF = 103,
}

/** C2C订单相关 */
export enum C2COrderReason {
  /** C2C挂单冻结 */
  FREEZE = 201,
  /** C2C挂单解冻 */
  RELEASE = 202,
  /** C2C挂单核销 */
  WRITE_OFF = 203,
  /** C2C购买成功(卖方挂单核销给用户) */
  WRITE_OFF_TO_USER = 204,
  /** 快速卖币冻结 */
  FAST_SELL = 207,
  /** 快速卖币解冻 */
  CANCEL_FAST_SELL = 208,
  /** 快速卖币核销 */
  FAST_SELL_WRITE_OFF = 209,
  /** 码商佣金 */
  AGENT_SELL_COMMISSION = 216,
}

/** C2C广告相关 */
export enum C2CAdsReason {
  /** C2C广告冻结 */
  FREEZE = 301,
  /** C2C广告解冻 */
  RELEASE = 302,
  /** C2C广告核销 */
  WRITE_OFF = 303,
}

/** 人工操作相关 */
export enum ManualReason {
  /** 人工加款 */
  INCR = 900,
  /** 人工扣款 */
  DECR = 901,
}

/** 商户相关 */
export enum MerchantReason {
  /** 商户玩家充值：用户钱包->商户钱包（入） */
  MEMBER_TOPUP_IN = 1000,
  /** 商户玩家充值：用户钱包->商户钱包（出） */
  MEMBER_TOPUP_OUT = 1001,
  /** 商户玩家提款：商户钱包->用户钱包（入） */
  MEMBER_PAYOUT_IN = 1002,
  /** 商户玩家提款：商户钱包->用户钱包（出） */
  MEMBER_PAYOUT_OUT = 1003,
}

/** 码商申请相关 */
export enum AgentApplyReason {
  /** 用户-码商，冻结保证金 */
  FREEZE = 1011,
  /** 冻结后业务失败，自动解冻 */
  ERR_RELEASE = 1012,
  /** 用户-码商，审核拒绝，解冻保证金 */
  REJECT = 1013,
  /** 码商-用户，解冻保证金 */
  REFUND = 1014,
}

/** 维权相关 */
export enum AppealReason {
  /** 维权冻结 */
  FREEZE = 212,
  /** 维权解冻 */
  RELEASE = 213,
  /** 维权成功 */
  SUCCESS = 214,
  /** 维权失败 */
  FAIL = 215,
}

/** 集成所有账变原因的命名空间 */
export namespace FlowReason {
  export import Deposit = DepositReason
  export import Withdraw = WithdrawReason
  export import C2COrder = C2COrderReason
  export import C2CAds = C2CAdsReason
  export import Manual = ManualReason
  export import Merchant = MerchantReason
  export import AgentApply = AgentApplyReason
}

/** 所有账变原因值的联合类型 */
export type FlowReasonValue =
  | DepositReason
  | WithdrawReason
  | C2COrderReason
  | C2CAdsReason
  | ManualReason
  | MerchantReason
  | AgentApplyReason

/** 账变原因描述映射 */
export const FlowReasonMap: Record<number, string> = {
  /** 充值 / 转账 */
  [DepositReason.DEPOSIT]: 'USDT兑换',
  [DepositReason.TRANSFER_OUT]: '用户转出',
  [DepositReason.TRANSFER_IN]: '用户转入',

  /** 提现相关 */
  [WithdrawReason.FREEZE]: '提现',
  [WithdrawReason.RELEASE]: '提现失败',
  [WithdrawReason.WRITE_OFF]: '提现核销',

  /** C2C订单相关 */
  [C2COrderReason.FREEZE]: '订单冻结',
  [C2COrderReason.RELEASE]: '订单解冻',
  [C2COrderReason.WRITE_OFF]: '订单核销',
  [C2COrderReason.WRITE_OFF_TO_USER]: 'C2C买币',

  /** C2C广告相关 */
  [C2CAdsReason.FREEZE]: '广告售卖',
  [C2CAdsReason.RELEASE]: '取消广告',
  [C2CAdsReason.WRITE_OFF]: '广告核销',
  [C2COrderReason.FAST_SELL]: '快速卖币',
  [C2COrderReason.CANCEL_FAST_SELL]: '卖币取消',
  [C2COrderReason.FAST_SELL_WRITE_OFF]: '快速卖币核销',
  [C2COrderReason.AGENT_SELL_COMMISSION]: '码商佣金',

  /** 人工操作相关 */
  [ManualReason.INCR]: '平台上分',
  [ManualReason.DECR]: '平台下分',

  /** 商户相关 */
  [MerchantReason.MEMBER_TOPUP_IN]: '商户转账',
  [MerchantReason.MEMBER_TOPUP_OUT]: '商户转账',
  [MerchantReason.MEMBER_PAYOUT_IN]: '商户转账',
  [MerchantReason.MEMBER_PAYOUT_OUT]: '商户转账',

  /** 码商申请相关 */
  [AgentApplyReason.FREEZE]: '预扣保证金',
  [AgentApplyReason.ERR_RELEASE]: '退回保证金',
  [AgentApplyReason.REJECT]: '退回保证金',
  [AgentApplyReason.REFUND]: '退回保证金',

  /** 维权相关 */
  [AppealReason.FREEZE]: '维权冻结',
  [AppealReason.RELEASE]: '维权解冻',
  [AppealReason.SUCCESS]: '维权成功',
  [AppealReason.FAIL]: '维权失败',
}

/** 获取账变原因描述 */
export function getFlowReasonText(reason: number): string {
  return FlowReasonMap[reason] || '未知类型'
}

/** 判断是否为收入 */
export function isIncome(reason: number): boolean {
  return [
    DepositReason.DEPOSIT,
    DepositReason.TRANSFER_IN,
    WithdrawReason.RELEASE,
    C2COrderReason.RELEASE,
    C2COrderReason.WRITE_OFF_TO_USER,
    C2COrderReason.AGENT_SELL_COMMISSION,
    C2CAdsReason.RELEASE,
    ManualReason.INCR,
    MerchantReason.MEMBER_PAYOUT_IN,
    AgentApplyReason.ERR_RELEASE,
    AgentApplyReason.REJECT,
    AgentApplyReason.REFUND,
  ].includes(reason)
}

/** 账单筛选项 */
export interface BillFilterItem {
  key: string
  label: string
  codes: number[]
}

export const BillFilters: BillFilterItem[] = [
  { key: 'all', label: '全部', codes: [] },
  { key: 'adsSell', label: '广告售卖', codes: [C2CAdsReason.FREEZE] },
  { key: 'adsCancel', label: '取消广告', codes: [C2CAdsReason.RELEASE] },
  { key: 'transferIn', label: '用户转入', codes: [DepositReason.TRANSFER_IN] },
  { key: 'transferOut', label: '用户转出', codes: [DepositReason.TRANSFER_OUT] },
  { key: 'usdtExchange', label: 'USDT兑换', codes: [DepositReason.DEPOSIT] },
  { key: 'c2cBuy', label: 'C2C买币', codes: [C2COrderReason.WRITE_OFF_TO_USER] },
  { key: 'depositFreeze', label: '预扣保证金', codes: [AgentApplyReason.FREEZE] },
  {
    key: 'depositRefund',
    label: '退回保证金',
    codes: [AgentApplyReason.ERR_RELEASE, AgentApplyReason.REJECT, AgentApplyReason.REFUND],
  },
  { key: 'platformIncr', label: '平台上分', codes: [ManualReason.INCR] },
  { key: 'platformDecr', label: '平台下分', codes: [ManualReason.DECR] },
  {
    key: 'merchantTransfer',
    label: '商户转账',
    codes: [MerchantReason.MEMBER_TOPUP_OUT, MerchantReason.MEMBER_PAYOUT_IN],
  },
  /** TODO: 转入转出失败暂无 codes */
  // { key: 'transferFailed', label: '转入转出失败', codes: [] },
  {
    key: 'fastSell',
    label: '快速卖币',
    codes: [
      C2COrderReason.FAST_SELL,
      C2COrderReason.CANCEL_FAST_SELL,
      C2COrderReason.FAST_SELL_WRITE_OFF,
    ],
  },
  {
    key: 'appealFreeze',
    label: '维权',
    codes: [AppealReason.FREEZE, AppealReason.RELEASE, AppealReason.SUCCESS, AppealReason.FAIL],
  },
  { key: 'agentCommission', label: '码商佣金', codes: [C2COrderReason.AGENT_SELL_COMMISSION] },
]
